import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  Avatar,
  alpha,
  Button,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CardActions
} from '@mui/material';
import {
  LocalBar as BarIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  Receipt as ReceiptIcon,
  History as HistoryIcon,
  Restaurant as RestaurantIcon,
  Event as EventIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  ShoppingCart as ShoppingCartIcon,
  Undo as UndoIcon
} from '@mui/icons-material';
import { barService } from '../services/bar';
import { useAuth } from '../contexts/AuthContext';
import { resolveMediaUrl } from '../utils/mediaUrl';

const Bar = () => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State
  const [statistics, setStatistics] = useState(null);
  const [drinks, setDrinks] = useState([]);
  const [userTransactions, setUserTransactions] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [activeEventSummary, setActiveEventSummary] = useState(null);
  
  // Ordering state
  const [orderingDrink, setOrderingDrink] = useState(null);
  const [quantities, setQuantities] = useState({});
  const [successMessage, setSuccessMessage] = useState(null);
  const [undoDialog, setUndoDialog] = useState({ open: false, transaction: null });

  useEffect(() => {
    // Redirect to login if not authenticated
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
      return;
    }
    
    // Only load data if authenticated
    if (!authLoading && isAuthenticated) {
      loadBarData();
    }
  }, [authLoading, isAuthenticated, navigate]);

  const loadBarData = async () => {
    setLoading(true);
    setError(null);

    try {
      const [statsResult, drinksResult, transactionsResult, recentOrdersResult, eventSummaryResult] = await Promise.all([
        barService.getUserStatistics(),
        barService.getDrinks(),
        barService.getMyTransactions(), // Use the new user endpoint
        barService.getRecentOrders(3),
        barService.getActiveEventSummary()
      ]);

      if (statsResult.success) {
        setStatistics(statsResult.data);
      } else {
        console.error('Failed to load statistics:', statsResult.error);
      }

      if (drinksResult.success) {
        setDrinks(drinksResult.data.filter(d => d.available));
      } else {
        setError(drinksResult.error);
      }

      if (transactionsResult.success) {
        setUserTransactions(transactionsResult.data);
      } else {
        console.error('Failed to load transactions:', transactionsResult.error);
      }

      if (recentOrdersResult.success) {
        setRecentOrders(recentOrdersResult.data);
      } else {
        console.error('Failed to load recent orders:', recentOrdersResult.error);
      }

      if (eventSummaryResult.success && eventSummaryResult.data) {
        setActiveEventSummary(eventSummaryResult.data);
      } else {
        console.error('Failed to load active event summary:', eventSummaryResult.error);
      }
    } catch (err) {
      setError('Failed to load bar data');
      console.error('Bar data loading error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const past = new Date(timestamp);
    const diffInSeconds = Math.floor((now - past) / 1000);

    if (diffInSeconds < 60) return `${diffInSeconds} seconds ago`;
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    return `${Math.floor(diffInSeconds / 86400)} days ago`;
  };

  // Group drinks by category
  const getDrinksByCategory = () => {
    const categories = {};
    drinks.forEach(drink => {
      if (!categories[drink.category]) {
        categories[drink.category] = [];
      }
      categories[drink.category].push(drink);
    });
    return categories;
  };

  const drinksByCategory = getDrinksByCategory();

  const handleIncreaseQuantity = (drinkId) => {
    setQuantities(prev => ({
      ...prev,
      [drinkId]: (prev[drinkId] || 1) + 1
    }));
  };

  const handleDecreaseQuantity = (drinkId) => {
    setQuantities(prev => ({
      ...prev,
      [drinkId]: Math.max(1, (prev[drinkId] || 1) - 1)
    }));
  };

  const handleOrder = async (drink) => {
    setOrderingDrink(drink._id);
    const quantity = quantities[drink._id] || 1;
    
    const result = await barService.addMyTransaction(drink._id, quantity);
    
    if (result.success) {
      setSuccessMessage(`Successfully ordered ${quantity}x ${drink.name}!`);
      setQuantities({ ...quantities, [drink._id]: 1 }); // Reset quantity
      loadBarData(); // Refresh all data
    } else {
      setError(result.error);
    }
    
    setOrderingDrink(null);
  };

  const handleUndoTransaction = async () => {
    const result = await barService.deleteMyTransaction(undoDialog.transaction._id);
    
    if (result.success) {
      setSuccessMessage('Order cancelled successfully!');
      setUndoDialog({ open: false, transaction: null });
      loadBarData();
    } else {
      setError(result.error);
      setUndoDialog({ open: false, transaction: null });
    }
  };

  if (authLoading || loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading bar information...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <IconButton
          onClick={loadBarData}
          color="primary"
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <RefreshIcon />
          <Typography>Retry</Typography>
        </IconButton>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <BarIcon sx={{ fontSize: 48, color: 'secondary.main' }} />
            <Box>
              <Typography variant="h3" component="h1" sx={{ fontWeight: 700 }}>
                Bar & Catering
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Track your spending, view the menu, and see what's popular
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={loadBarData}
            sx={{ 
              bgcolor: 'background.paper',
              '&:hover': { bgcolor: 'action.hover' }
            }}
            size="large"
          >
            <RefreshIcon fontSize="large" />
          </IconButton>
        </Box>
      </Box>

      <Container maxWidth="xl" disableGutters>
        <Grid container spacing={3}>
          {/* Statistics Section */}
          <Grid item xs={12}>
            <Paper 
              elevation={2} 
              sx={{ 
                p: 3,
                backgroundColor: '#241E18',
                border: '1px solid rgba(201, 162, 39, 0.1)',
                transition: 'all 0.3s ease',
                '&:hover': {
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <TrendingUpIcon color="primary" />
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  Your Statistics
                </Typography>
              </Box>
              
              {statistics ? (
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card 
                      sx={{ 
                        height: '100%',
                        backgroundColor: '#241E18',
                        border: `1px solid ${alpha('#C9A227', 0.2)}`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: `0 8px 24px ${alpha('#C9A227', 0.3)}`
                        }
                      }}
                    >
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                          {statistics.total_spent.toFixed(2)} CHF
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Total Spent
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card 
                      sx={{ 
                        height: '100%',
                        backgroundColor: '#241E18',
                        border: `1px solid ${alpha('#3D7A8C', 0.2)}`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: `0 8px 24px ${alpha('#3D7A8C', 0.3)}`
                        }
                      }}
                    >
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
                          {statistics.transaction_count}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Transactions
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card 
                      sx={{ 
                        height: '100%',
                        backgroundColor: '#241E18',
                        border: `1px solid ${alpha('#6B8F71', 0.2)}`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: `0 8px 24px ${alpha('#6B8F71', 0.3)}`
                        }
                      }}
                    >
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                          {statistics.favorite_drink || 'N/A'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Favorite Drink
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card
                      sx={{ 
                        height: '100%',
                        backgroundColor: '#241E18',
                        border: `1px solid ${alpha('#3D7A8C', 0.2)}`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: `0 8px 24px ${alpha('#3D7A8C', 0.3)}`
                        }
                      }}
                    >
                      <CardContent>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'info.main' }}>
                          By Category
                        </Typography>
                        {Object.entries(statistics.spending_by_category || {}).length > 0 ? (
                          Object.entries(statistics.spending_by_category).map(([category, amount]) => (
                            <Box key={category} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="body2">{category}:</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {amount.toFixed(2)} CHF
                              </Typography>
                            </Box>
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No data yet
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              ) : (
                <Alert severity="info">No statistics available yet. Start ordering to see your stats!</Alert>
              )}
            </Paper>
          </Grid>

          {/* Active Event Summary Section */}
          {activeEventSummary && (
            <Grid item xs={12}>
              <Paper 
                elevation={2} 
                sx={{ 
                  p: 3,
                  backgroundColor: '#241E18',
                  border: '1px solid rgba(201, 162, 39, 0.1)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <EventIcon color="primary" />
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    Active Event: {activeEventSummary.event_name}
                  </Typography>
                </Box>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={4}>
                    <Card 
                      sx={{ 
                        height: '100%',
                        backgroundColor: '#241E18',
                        border: `1px solid ${alpha('#6B8F71', 0.2)}`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: `0 8px 24px ${alpha('#6B8F71', 0.3)}`
                        }
                      }}
                    >
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                          {activeEventSummary.total_revenue.toFixed(2)} CHF
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Total Revenue
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card 
                      sx={{ 
                        height: '100%',
                        backgroundColor: '#241E18',
                        border: `1px solid ${alpha('#3D7A8C', 0.2)}`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: `0 8px 24px ${alpha('#3D7A8C', 0.3)}`
                        }
                      }}
                    >
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'info.main' }}>
                          {activeEventSummary.total_transactions}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Total Orders
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card 
                      sx={{ 
                        height: '100%',
                        backgroundColor: '#241E18',
                        border: `1px solid ${alpha('#C9A227', 0.2)}`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: `0 8px 24px ${alpha('#C9A227', 0.3)}`
                        }
                      }}
                    >
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                          {activeEventSummary.user_summaries.length}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Participants
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Top Spenders for Active Event */}
                {activeEventSummary.user_summaries.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                      Top Spenders
                    </Typography>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Rank</strong></TableCell>
                            <TableCell><strong>User</strong></TableCell>
                            <TableCell align="center"><strong>Orders</strong></TableCell>
                            <TableCell align="right"><strong>Total Spent</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {activeEventSummary.user_summaries
                            .sort((a, b) => b.total_spent - a.total_spent)
                            .slice(0, 5)
                            .map((userSummary, index) => (
                              <TableRow 
                                key={userSummary.user_id}
                                sx={{
                                  '&:hover': {
                                    bgcolor: 'action.hover'
                                  }
                                }}
                              >
                                <TableCell>
                                  <Chip 
                                    label={`#${index + 1}`} 
                                    color={index === 0 ? 'warning' : index === 1 ? 'default' : 'default'}
                                    size="small"
                                    sx={{ fontWeight: 'bold' }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Avatar 
                                      sx={{ width: 24, height: 24 }}
                                      src={resolveMediaUrl(userSummary.avatar)}
                                    >
                                      {userSummary.username?.charAt(0).toUpperCase()}
                                    </Avatar>
                                    <Typography variant="body2">
                                      {userSummary.username}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell align="center">{userSummary.transaction_count}</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                  {userSummary.total_spent.toFixed(2)} CHF
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </Paper>
            </Grid>
          )}

          {/* Menu Section */}
          <Grid item xs={12} md={6}>
            <Paper 
              elevation={2} 
              sx={{ 
                p: 3, 
                height: '100%',
                backgroundColor: '#241E18',
                border: '1px solid rgba(201, 162, 39, 0.1)',
                transition: 'all 0.3s ease',
                '&:hover': {
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <RestaurantIcon color="primary" />
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  Full Menu
                </Typography>
              </Box>
              
              {drinks.length === 0 ? (
                <Alert severity="info">No drinks available at the moment.</Alert>
              ) : (
                <Box>
                  {Object.keys(drinksByCategory).sort().map((category) => (
                    <Box key={category} sx={{ mb: 4 }}>
                      {/* Category Header */}
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontWeight: 'bold', 
                          mb: 2,
                          pb: 1,
                          borderBottom: '2px solid',
                          borderColor: 'primary.main',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1
                        }}
                      >
                        <RestaurantIcon color="primary" />
                        {category}
                        <Chip 
                          label={`${drinksByCategory[category].length} items`} 
                          size="small" 
                          sx={{ ml: 1 }}
                        />
                      </Typography>
                      
                      {/* Drinks in this category */}
                      <Grid container spacing={2}>
                        {drinksByCategory[category].map((drink) => (
                          <Grid item xs={12} sm={6} md={4} key={drink._id}>
                            <Card 
                              elevation={1}
                              sx={{
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  boxShadow: 3
                                }
                              }}
                            >
                              <CardContent sx={{ flexGrow: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                  <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                      {drink.name}
                                    </Typography>
                                  </Box>
                                  <Chip
                                    label={`${drink.price.toFixed(2)} CHF`}
                                    color="primary"
                                    sx={{ fontWeight: 'bold' }}
                                  />
                                </Box>
                                
                                {/* Quantity selector */}
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
                                  <IconButton 
                                    size="small"
                                    onClick={() => handleDecreaseQuantity(drink._id)}
                                    sx={{ 
                                      bgcolor: 'action.hover',
                                      '&:hover': { bgcolor: 'action.selected' }
                                    }}
                                  >
                                    <RemoveIcon />
                                  </IconButton>
                                  <Typography variant="h6" sx={{ minWidth: '40px', textAlign: 'center', fontWeight: 'bold' }}>
                                    {quantities[drink._id] || 1}
                                  </Typography>
                                  <IconButton 
                                    size="small"
                                    onClick={() => handleIncreaseQuantity(drink._id)}
                                    sx={{ 
                                      bgcolor: 'action.hover',
                                      '&:hover': { bgcolor: 'action.selected' }
                                    }}
                                  >
                                    <AddIcon />
                                  </IconButton>
                                </Box>
                              </CardContent>
                              
                              <CardActions sx={{ p: 2, pt: 0 }}>
                                <Button
                                  variant="contained"
                                  fullWidth
                                  onClick={() => handleOrder(drink)}
                                  disabled={orderingDrink === drink._id}
                                  startIcon={orderingDrink === drink._id ? <CircularProgress size={20} color="inherit" /> : <ShoppingCartIcon />}
                                  sx={{ py: 1.5 }}
                                >
                                  {orderingDrink === drink._id ? 'Ordering...' : 'Order Now'}
                                </Button>
                              </CardActions>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  ))}
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Recent Orders Section */}
          <Grid item xs={12} md={6}>
            <Paper 
              elevation={2} 
              sx={{ 
                p: 3, 
                height: '100%',
                backgroundColor: '#241E18',
                border: '1px solid rgba(201, 162, 39, 0.1)',
                transition: 'all 0.3s ease',
                '&:hover': {
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <HistoryIcon color="primary" />
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  Recent Orders (Global)
                </Typography>
              </Box>
              
              {recentOrders.length === 0 ? (
                <Alert severity="info">No recent orders yet.</Alert>
              ) : (
                <List>
                  {recentOrders.map((order, index) => (
                    <React.Fragment key={order._id}>
                      {index > 0 && <Divider />}
                      <ListItem>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                {order.username}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                ordered
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                {order.quantity}x {order.drink_name}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary">
                              {formatTimeAgo(order.timestamp)} - {(order.price * order.quantity).toFixed(2)} CHF
                            </Typography>
                          }
                        />
                      </ListItem>
                    </React.Fragment>
                  ))}
                </List>
              )}
            </Paper>
          </Grid>

          {/* User Purchase History */}
          <Grid item xs={12}>
            <Paper 
              elevation={2} 
              sx={{ 
                p: 3,
                backgroundColor: '#241E18',
                border: '1px solid rgba(201, 162, 39, 0.1)',
                transition: 'all 0.3s ease',
                '&:hover': {
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <ReceiptIcon color="primary" />
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  Your Purchase History
                </Typography>
              </Box>
              
              {userTransactions.length === 0 ? (
                <Alert severity="info">You haven't made any purchases yet.</Alert>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Date</strong></TableCell>
                        <TableCell><strong>Drink</strong></TableCell>
                        <TableCell align="center"><strong>Quantity</strong></TableCell>
                        <TableCell align="right"><strong>Price</strong></TableCell>
                        <TableCell align="right"><strong>Total</strong></TableCell>
                        <TableCell align="center"><strong>Actions</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {userTransactions.slice(0, 50).map((transaction, index) => (
                        <TableRow 
                          key={transaction._id}
                          sx={{
                            bgcolor: index % 2 === 0 ? 'background.default' : 'transparent',
                            '&:hover': {
                              bgcolor: 'action.hover'
                            }
                          }}
                        >
                          <TableCell>
                            {new Date(transaction.timestamp).toLocaleDateString()} {new Date(transaction.timestamp).toLocaleTimeString()}
                          </TableCell>
                          <TableCell>{transaction.drink_name}</TableCell>
                          <TableCell align="center">{transaction.quantity}</TableCell>
                          <TableCell align="right">{transaction.price.toFixed(2)} CHF</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            {(transaction.price * transaction.quantity).toFixed(2)} CHF
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setUndoDialog({ open: true, transaction })}
                              title="Cancel this order"
                            >
                              <UndoIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              
              {userTransactions.length > 50 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Showing the most recent 50 transactions. You have {userTransactions.length} total transactions.
                </Alert>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSuccessMessage(null)} 
          severity="success" 
          sx={{ width: '100%' }}
          variant="filled"
        >
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Undo Confirmation Dialog */}
      <Dialog 
        open={undoDialog.open} 
        onClose={() => setUndoDialog({ open: false, transaction: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cancel Order?</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Are you sure you want to cancel this order?
          </Typography>
          {undoDialog.transaction && (
            <Box sx={{ 
              p: 2, 
              bgcolor: 'action.hover', 
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider'
            }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Drink:</strong> {undoDialog.transaction.drink_name}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Quantity:</strong> {undoDialog.transaction.quantity}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Total:</strong> {(undoDialog.transaction.price * undoDialog.transaction.quantity).toFixed(2)} CHF
              </Typography>
              <Typography variant="body2">
                <strong>Date:</strong> {new Date(undoDialog.transaction.timestamp).toLocaleString()}
              </Typography>
            </Box>
          )}
          <Alert severity="warning" sx={{ mt: 2 }}>
            This action cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUndoDialog({ open: false, transaction: null })}>
            Keep Order
          </Button>
          <Button 
            onClick={handleUndoTransaction} 
            color="error" 
            variant="contained"
            startIcon={<UndoIcon />}
          >
            Cancel Order
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Bar;

